import { supabase, AporteCampaña } from '../lib/supabase';
import type { Database } from '../types/database';
import type { ComprobanteResumen, RespaldoMovimiento } from '../types/comprobante';
import type { CambiosCompromisoCampana } from '../types/fondosCampana';
import { lanzarErrorOperacionCampana, ResultadoCampanaInciertoError, validarIdentificadorOperacionCampana } from '../utils/fondosCampanaRpc';
import { SELECT_MOVIMIENTO_CON_COMPROBANTES } from './movimientoService';

const TABLA_APORTES = 'aportes_campaña';

function normalizarAporte(aporte: Omit<AporteCampaña, 'monto_aportado'> & {
  readonly monto_aportado: number | null;
}): AporteCampaña {
  return { ...aporte, monto_aportado: aporte.monto_aportado ?? 0 };
}

// Cada pago es un ingreso del Lumisial; con el resumen de su comprobante para el historial.
export type PagoCampana = Database['public']['Tables']['movimientos']['Row'] & {
  readonly comprobante?: ComprobanteResumen | null;
};

async function leerPagos(filtro: { readonly aporteId: string } | { readonly campanaId: string }): Promise<PagoCampana[]> {
  const pagos: PagoCampana[] = [];
  const cantidad = 500;
  for (let desde = 0; ; desde += cantidad) {
    let query = supabase.from('movimientos').select(SELECT_MOVIMIENTO_CON_COMPROBANTES).eq('tipo', 'ingreso')
      .order('created_at', { ascending: false }).order('id', { ascending: false }).range(desde, desde + cantidad - 1);
    if ('aporteId' in filtro) query = query.eq('aporte_campana_id', filtro.aporteId);
    else query = query.eq('origen_tipo', 'campaña').eq('origen_id', filtro.campanaId).not('aporte_campana_id', 'is', null);
    const { data, error } = await query;
    if (error) throw error;
    // La constante del select no es literal, así que Supabase no puede inferir el tipo de la fila.
    const pagina = (data ?? []) as unknown as PagoCampana[];
    pagos.push(...pagina);
    if (pagina.length < cantidad) return pagos;
  }
}

export const aporteCampanaService = {
  async getByCampana(campanaId: string): Promise<AporteCampaña[]> {
    const { data, error } = await supabase
      .from(TABLA_APORTES)
      .select('*')
      .eq('campaña_id', campanaId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return (data ?? []).map(normalizarAporte);
  },

  async getById(id: string): Promise<AporteCampaña | null> {
    const { data, error } = await supabase
      .from(TABLA_APORTES)
      .select('*')
      .eq('id', id)
      .maybeSingle();

    if (error) throw error;
    return data ? normalizarAporte(data) : null;
  },

  async createCompromiso(aporte: Omit<AporteCampaña, 'id' | 'created_at'>): Promise<AporteCampaña> {
    const { data, error } = await supabase
      .from(TABLA_APORTES)
      .insert(aporte)
      .select()
      .single();

    if (error) throw error;
    return normalizarAporte(data);
  },

  /** Pago de un compromiso con su respaldo: regla general (adjunto o "subir después"). */
  async registrarPago(
    aporteId: string, monto: number, solicitudId: string, respaldo: RespaldoMovimiento,
  ): Promise<string> {
    const { data, error } = await supabase.rpc('registrar_pago_aporte_campana', {
      p_aporte_id: aporteId, p_monto: monto, p_solicitud_id: solicitudId,
      p_medio_pago: respaldo.medio_pago, p_folio_recibo: respaldo.folio_recibo,
      p_comprobante_id: respaldo.comprobante_id,
      p_comprobante_pendiente: respaldo.comprobante_pendiente_desde !== null,
    }).then(undefined, lanzarErrorOperacionCampana);
    if (error) lanzarErrorOperacionCampana(error);
    return validarIdentificadorOperacionCampana(data);
  },

  async registrarEntregaObjeto(aporteId: string, totalAnterior: number, totalEntregado: number): Promise<string> {
    const { data, error } = await supabase.rpc('registrar_entrega_objeto_campana', {
      p_aporte_id: aporteId, p_total_anterior: totalAnterior, p_total_entregado: totalEntregado,
    }).then(undefined, lanzarErrorOperacionCampana);
    if (error) lanzarErrorOperacionCampana(error);
    return validarIdentificadorOperacionCampana(data);
  },

  async getPagos(aporteId: string): Promise<PagoCampana[]> {
    return leerPagos({ aporteId });
  },

  async getPagosDeCampana(campanaId: string): Promise<PagoCampana[]> {
    return leerPagos({ campanaId });
  },

  async anularPago(movimientoId: string, motivo: string): Promise<boolean> {
    const { data, error } = await supabase.rpc('anular_pago_aporte_campana', {
      p_movimiento_id: movimientoId, p_motivo: motivo,
    }).then(undefined, lanzarErrorOperacionCampana);
    if (error) lanzarErrorOperacionCampana(error);
    if (typeof data !== 'boolean') throw new ResultadoCampanaInciertoError();
    return data;
  },

  async update(id: string, aporte: CambiosCompromisoCampana): Promise<AporteCampaña> {
    const { data, error } = await supabase.rpc('editar_compromiso_campana', {
      p_aporte_id: id, p_cambios: aporte,
    }).then(undefined, lanzarErrorOperacionCampana);
    if (error) throw error;
    if (!data) throw new Error('No se recibió el compromiso editado.');
    return normalizarAporte(data);
  },

  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from(TABLA_APORTES)
      .delete()
      .eq('id', id).select('id').single();

    if (error) throw error;
  },

  async getTotalesByCampana(campanaId: string): Promise<{
    totalComprometido: number;
    totalAportado: number;
    diferencia: number;
  }> {
    const { data, error } = await supabase
      .from(TABLA_APORTES)
      .select('monto_comprometido, monto_aportado')
      .eq('campaña_id', campanaId);

    if (error) throw error;

    const totalComprometido = data?.reduce((sum, a) => sum + Number(a.monto_comprometido), 0) || 0;
    const totalAportado = data?.reduce((sum, a) => sum + Number(a.monto_aportado), 0) || 0;

    return {
      totalComprometido,
      totalAportado,
      diferencia: totalComprometido - totalAportado
    };
  }
};
